import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { WorkerDto } from "@normax-performance/shared-types";
import { DateField, SearchCombobox, type ComboboxOption } from "../../../shared-ui/primitives";
import { formatCodeAndName } from "../../../lib/format";
import "./EntryHeader.css";

export interface EntryHeaderProps {
  workDate: string;
  onWorkDateChange: (isoDate: string) => void;
  workerId: string | null;
  onWorkerChange: (workerId: string | null) => void;
  workers: WorkerDto[];
  disabled?: boolean;
}

export function EntryHeader({
  workDate,
  onWorkDateChange,
  workerId,
  onWorkerChange,
  workers,
  disabled,
}: EntryHeaderProps) {
  const { t: tCommon } = useTranslation("common");
  const workerOptions = useMemo<ComboboxOption[]>(
    () =>
      workers.map((w) => {
        const name = `${w.lastName} ${w.firstName}`;
        return {
          id: w.id,
          // "CODE — Prezime Ime" whenever the worker has a code — fast
          // search (NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md
          // §10). employeeCode is now always searchable via businessCode
          // below (previously only shown/searchable when hasNamesake was
          // true — the department fallback below still covers namesake
          // disambiguation for workers without a code).
          label: formatCodeAndName(w.employeeCode, name),
          // Department disambiguator preserved exactly as before (shown
          // whenever set, regardless of namesake) — only the redundant
          // "show code in description too" branch is dropped, since the
          // code is now always visible in the label itself.
          description: w.homeDepartmentName ?? undefined,
          businessCode: w.employeeCode,
        };
      }),
    [workers],
  );

  return (
    <div className="nx-entry-header">
      <div className="nx-entry-header__field">
        <DateField
          id="work-date"
          label="Datum"
          value={workDate}
          disabled={disabled}
          onChange={(iso) => iso && onWorkDateChange(iso)}
          invalidFormatMessage={tCommon("dateField.invalidFormat")}
          calendarButtonLabel={tCommon("dateField.openCalendar")}
          previousMonthLabel={tCommon("dateField.previousMonth")}
          nextMonthLabel={tCommon("dateField.nextMonth")}
        />
      </div>
      <div className="nx-entry-header__field nx-entry-header__field--wide">
        <SearchCombobox
          id="worker-combobox"
          label="Radnik"
          items={workerOptions}
          selectedKey={workerId}
          onSelectionChange={onWorkerChange}
          placeholder="Pretraži radnika..."
          isDisabled={disabled}
          emptyMessage="Nema unetih radnika u sistemu."
        />
      </div>
    </div>
  );
}
